/**
 * @copyright Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React from 'react';
import styled from 'styled-components';
import tokens from '../../../../../common/styles/design-tokens';
import { APP_COLORS } from '../../../../../common/Constants';

const FilterDiscoverySearchTypePills = (props) => {
    const { options, stateObj, handleSubmitFilter, handleRemoveFilter } = props;

    if (!options?.length) return null;

    // Pills default to on. universeSlice stores the full filter object at
    // entities[id] (not just the value), so we read `.value` — absent entry
    // or `.value !== false` means checked. Only explicit `value: false`
    // means off. Builder treats "all on" as no scope restriction.
    const isPillOn = (id) => stateObj?.[id]?.value !== false;
    const onCount = options.reduce((n, o) => (isPillOn(o.id) ? n + 1 : n), 0);

    const toggle = (ftOption) => {
        if (ftOption.isLocked) return;
        const { id } = ftOption;
        const isOn = isPillOn(id);
        if (isOn) {
            // Keep at least one scope active — the last on pill can't be unchecked.
            if (onCount <= 1) return;
            handleSubmitFilter({ ...ftOption, value: false });
        } else {
            handleRemoveFilter({ id });
        }
    };

    return (
        <Wrapper>
            <SectionLabel>Search across:</SectionLabel>
            <PillRow role="list">
                {options.map((ftOption) => {
                    const { id, label, isLocked } = ftOption;
                    const isOn = isPillOn(id);
                    const isLastOn = isOn && onCount <= 1;
                    const disabled = !!isLocked || isLastOn;
                    return (
                        <Pill
                            key={id}
                            type="button"
                            role="listitem"
                            $active={isOn}
                            $disabled={disabled}
                            disabled={disabled}
                            aria-pressed={isOn}
                            title={isLastOn ? 'At least one search scope must stay selected' : undefined}
                            onClick={() => toggle(ftOption)}
                        >
                            {label}
                        </Pill>
                    );
                })}
            </PillRow>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacing.xs};
`;

const SectionLabel = styled.div`
    font-weight: ${tokens.typography.fontWeight.semibold};
    font-size: 11px;
    color: ${APP_COLORS['tech-blue']};
    text-transform: none;
    letter-spacing: 0.02em;
`;

const PillRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${tokens.spacing.xs};
`;

const Pill = styled.button`
    border-radius: 999px;
    padding: 0.2rem 0.65rem;
    font-size: ${tokens.typography.fontSize.xs};
    font-weight: 600;
    letter-spacing: 0.02em;
    line-height: 1.35;
    cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(p) => (p.$disabled ? 0.55 : 1)};
    transition:
        background-color 0.15s ease,
        color 0.15s ease,
        border-color 0.15s ease;

    ${(p) =>
        p.$active
            ? `
        background-color: ${APP_COLORS['tech-blue']};
        color: ${tokens.colors.white};
        border: 1px solid ${APP_COLORS['tech-blue']};
    `
            : `
        background-color: ${tokens.colors['gray-100']};
        color: ${tokens.colors['gray-800']};
        border: 1px solid ${tokens.colors['gray-300']};
    `}

    &:hover:not(:disabled) {
        border-color: ${APP_COLORS['tech-blue']};
        color: ${(p) => (p.$active ? tokens.colors.white : APP_COLORS['tech-blue'])};
    }
`;

export default FilterDiscoverySearchTypePills;
